import { cn } from "@/lib/utils";
import { 
  Shield, 
  AlertTriangle, 
  TrendingUp, 
  Zap,
  CheckCircle,
  CircleDot,
  Circle
} from "lucide-react";
import { useTranslation } from "react-i18next";

type BadgeVariant = 'confidence' | 'risk' | 'edge' | 'combo';
type ConfidenceLevel = 'high' | 'medium' | 'low';
type RiskLevel = 'low' | 'medium' | 'high';

interface SG7BadgeProps {
  variant: BadgeVariant;
  level?: ConfidenceLevel | RiskLevel;
  value?: string | number;
  responsive?: boolean;
  className?: string;
}

export function SG7Badge({ 
  variant, 
  level = 'medium',
  value,
  responsive = false,
  className 
}: SG7BadgeProps) {
  const { t } = useTranslation('analysis');

  const getVariantConfig = () => {
    switch (variant) {
      case 'confidence':
        const confColors = {
          high: 'text-[hsl(var(--sg7-confidence-high))] border-[hsl(var(--sg7-confidence-high)/0.3)]',
          medium: 'text-[hsl(var(--sg7-confidence-medium))] border-[hsl(var(--sg7-confidence-medium)/0.3)]',
          low: 'text-[hsl(var(--sg7-confidence-low))] border-[hsl(var(--sg7-confidence-low)/0.3)]'
        };
        const confIcons = {
          high: CheckCircle,
          medium: CircleDot,
          low: Circle
        };
        return {
          icon: confIcons[level as ConfidenceLevel],
          label: `${t('confidence.label')} ${t(`confidence.${level}`)}`,
          colorClass: confColors[level as ConfidenceLevel]
        };

      case 'risk':
        const riskColors = {
          low: 'text-[hsl(var(--sg7-risk-low))] border-[hsl(var(--sg7-risk-low)/0.3)]',
          medium: 'text-[hsl(var(--sg7-risk-medium))] border-[hsl(var(--sg7-risk-medium)/0.3)]',
          high: 'text-[hsl(var(--sg7-risk-high))] border-[hsl(var(--sg7-risk-high)/0.3)]'
        };
        return {
          icon: Shield,
          label: `${t('risk.label')} ${t(`risk.${level}`)}`,
          colorClass: riskColors[level as RiskLevel]
        };

      case 'edge':
        return {
          icon: TrendingUp,
          label: value ? `EV +${value}%` : 'Edge+',
          colorClass: 'text-[hsl(var(--sg7-gold))] border-[hsl(var(--sg7-gold)/0.3)]'
        };

      case 'combo':
        return {
          icon: Zap,
          label: 'Combo',
          colorClass: 'text-[hsl(var(--sg7-stripe-amber))] border-[hsl(var(--sg7-stripe-amber)/0.3)]'
        };

      default:
        return {
          icon: CircleDot,
          label: '',
          colorClass: 'text-[hsl(var(--sg7-text-muted))] border-[hsl(var(--sg7-stroke-subtle))]'
        };
    }
  };

  const config = getVariantConfig();
  const Icon = config.icon;

  return (
    <span
      className={cn(
        "inline-flex items-center",
        "text-[10px] font-semibold uppercase tracking-wide",
        responsive ? "p-1 sm:gap-1 sm:px-2 sm:py-0.5" : "gap-1 px-2 py-0.5",
        "rounded border",
        "bg-[hsl(var(--sg7-surface-2))]",
        config.colorClass,
        className
      )}
    >
      <Icon className="h-3 w-3" />
      {responsive ? (
        <span className="hidden sm:inline">{config.label}</span>
      ) : (
        config.label
      )}
    </span>
  );
}
